import {useMemo, useRef, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {AudienceRow} from 'shells/row/audience-row/audience-row';

import type {WidgetFragment} from 'nutshell-graphql-types';

import {AddToAudiencePopover} from '../../../../core/audiences/add-to-audience-popover';
import {CollapsibleCardWidget} from '../../../../core/card/collapsible-card-widget';
import {WidgetSeeAllFooter} from '../../../../core-entity/components/widget-see-all-footer';

import {useAddAudienceMembership} from './graphql/hooks/use-add-audience-membership';
import {useGetContactAudiences} from './graphql/hooks/use-get-contact-audiences';
import {useGetSmsEmailUnsubscribes} from './graphql/hooks/use-get-sms-email-unsubscribes';
import {useRemoveAudienceMembership} from './graphql/hooks/use-remove-audience-membership';
import {getAddButtonProps, getEmptyStateProps} from './helpers';

import styles from './audiences-widget.module.css';

type Props = {
    contactId: string;
    widget: WidgetFragment;
    user: User;
};

export function AudiencesWidget(props: Props) {
    const [error, setError] = useState<string | null | undefined>(null);
    const [showAddPopover, setShowAddPopover] = useState<boolean>(false);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const addButtonRef = useRef<HTMLButtonElement | null | undefined>(null);

    const {audiences, contact, isLoading} = useGetContactAudiences(props.contactId);
    const {isUnsubscribed, preferenceCenterLink} = useGetSmsEmailUnsubscribes(props.contactId);
    const {removeAudienceMembership} = useRemoveAudienceMembership(props.contactId);
    const {addAudienceMembership} = useAddAudienceMembership(props.contactId);

    const audiencesToShow = useMemo(
        () => (isExpanded ? audiences : audiences?.slice(0, 5)),
        [audiences, isExpanded]
    );

    const handleRemoveAudienceMembership = (audienceMemberId: string) => {
        removeAudienceMembership({audienceMemberId}).catch((error) => {
            setError(safelyGetGraphQLErrorMessage(error));
        });
    };

    const handleAddAudienceMembership = (audienceId: string) => {
        setShowAddPopover(false);
        setIsExpanded(true); // Expand to show the newly added audience

        return addAudienceMembership({audienceId, entityId: props.contactId}).catch((error) => {
            setError(safelyGetGraphQLErrorMessage(error));
        });
    };

    const canAccessMarketing = props.user.permissions.canAccessMarketing;
    const shouldShowAddButton = !isUnsubscribed && !isLoading && canAccessMarketing;

    const addButtonProps = getAddButtonProps(
        () => setShowAddPopover(true),
        (ref) => {
            addButtonRef.current = ref;
        },
        showAddPopover,
        shouldShowAddButton
    );

    const emptyStateProps = getEmptyStateProps(
        audiences?.length === 0,
        isUnsubscribed,
        preferenceCenterLink,
        canAccessMarketing
    );

    return (
        <>
            <CollapsibleCardWidget
                widget={props.widget}
                isLoading={isLoading}
                addButtonProps={addButtonProps}
                emptyStateProps={emptyStateProps}
                errorProps={{
                    error: error,
                    setError: setError,
                }}
                hasBorder={true}
            >
                <div className={styles['scroll-container']}>
                    <div className='mt-8'>
                        {audiencesToShow?.map((audience) => (
                            <AudienceRow
                                key={audience.id}
                                title={audience.audience.name}
                                isWebFX={audience.audience.isWebFX}
                                onRemove={() => handleRemoveAudienceMembership(audience.id)}
                            />
                        ))}
                    </div>
                </div>
                <WidgetSeeAllFooter
                    seeAllProps={{
                        isExpanded: isExpanded,
                        hasMore: (audiences?.length ?? 0) > 5,
                        term: 'audiences',
                        onClick: () => setIsExpanded(true),
                    }}
                />
            </CollapsibleCardWidget>
            {showAddPopover ? (
                <AddToAudiencePopover
                    entityType='contacts'
                    anchor={addButtonRef.current}
                    onCancel={() => setShowAddPopover(false)}
                    onSubmit={(input) => handleAddAudienceMembership(input.value)}
                    selectedCount={1}
                    showConfirmationPage={false}
                    itemName={contact?.name}
                    excludedAudienceIds={audiences?.map((audience) => audience.audience.id)}
                />
            ) : undefined}
        </>
    );
}
